import React, { useState, useEffect, useRef } from 'react';
import AppLoading from '../components/AppLoading';
import sharedAPIsService from '../hooks/sharedAPIs';
import cronServerAPIsService from '../hooks/cronServerAPIs';

interface HealthStatus {
  status: string;
  timestamp: string;
  uptime: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  environment: string;
  cronJobs: {
    worker: {
      lastExecution: string;
      executionCount: number;
      errorCount: number;
      schedule: string;
      nextExecution: string;
      status: string;
    };
    compliance: {
      lastExecution: string;
      executionCount: number;
      errorCount: number;
      schedule: string;
      nextExecution: string;
      status: string;
    };
    cleanup: {
      lastExecution: string;
      executionCount: number;
      errorCount: number;
      schedule: string;
      nextExecution: string;
      status: string;
    };
    completionSync: {
      lastExecution: string;
      executionCount: number;
      errorCount: number;
      schedule: string;
      nextExecution: string;
      status: string;
    };
  };
  database: {
    status: string;
    pool?: {
      used: number;
      free: number;
      pending: number;
      min: number;
      max: number;
    };
  };
  syncOperations: {
    isManualSyncInProgress: boolean;
    isCompletionSyncInProgress: boolean;
    isCleanupInProgress: boolean;
    isCompletionHistoryInProgress: boolean;
    isScheduledCronRunning: boolean;
    currentSyncOperation: string | null;
    syncStartTime: string | null;
    syncDuration: string | null;
    syncDurationHours: string | null;
    isStale: boolean;
    canStartNewSync: boolean;
    canStartScheduledCron: boolean;
  };
}

interface SyncStatus {
  success: boolean;
  syncStatus: {
    isManualSyncInProgress: boolean;
    isCompletionSyncInProgress: boolean;
    isCleanupInProgress: boolean;
    isCompletionHistoryInProgress: boolean;
    isScheduledCronRunning: boolean;
    currentSyncOperation: string | null;
    syncStartTime: string | null;
    syncDuration: string | null;
    syncDurationHours: string | null;
    isStale: boolean;
    canStartNewSync: boolean;
    canStartScheduledCron: boolean;
  };
  timestamp: string;
}

const CronServerPage: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operationInProgress, setOperationInProgress] = useState<string | null>(null);
  const [complianceDates, setComplianceDates] = useState({ from: '', to: '', dryRun: false });
  const [cronStatus, setCronStatus] = useState<'Active' | 'Offline'>('Offline');

  const [latestLogsFirst, setLatestLogsFirst] = useState<boolean>(true);
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null);
  const [completionLastSyncDate, setCompletionLastSyncDate] = useState<Date | null>(null);
  const [logsFetched, setLogsFetched] = useState<boolean>(false);
  const logsSectionRef = useRef<HTMLDivElement>(null);

  const scrollToLogs = () => {
    logsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fetchHealthStatus = async () => {
    try {
      const data = await cronServerAPIsService.getHealthStatus();
      setHealthStatus(data);
      setCronStatus('Active');
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to fetch health status:', err.message);
      
      // Check if it's a 500 error to determine offline status
      if (err.response?.status === 500) {
        setCronStatus('Offline');
      }
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const data = await cronServerAPIsService.getSyncStatus();
      setSyncStatus(data);
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to fetch sync status:', err.message);
    }
  };

  const fetchLogs = async (latest: boolean = false) => {
    try {
      setLogsLoading(true);
      const data = await cronServerAPIsService.getLogs(latest);
      setLogs(data);
      setLogsFetched(true);
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to fetch logs:', err.message);
      setLogsFetched(false);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchLastSyncDate = async () => {
    try {
      const response = await sharedAPIsService.getLastSyncedDate({});
      if (response.data?.lastSyncDate) {
        setLastSyncDate(new Date(response.data.lastSyncDate));
      }
      if (response.data?.completionLastSyncDate) {
        setCompletionLastSyncDate(new Date(response.data.completionLastSyncDate));
      }
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to fetch last sync date:', err.message);
    }
  };

  const handleFullSync = async () => {
    try {
      setOperationInProgress('full-sync');
      await cronServerAPIsService.triggerFullSync();
      setLogsFetched(true);
      await Promise.all([fetchLogs(latestLogsFirst), fetchSyncStatus()]);
      scrollToLogs();
    } catch (err: any) {
      console.warn('Failed to trigger full sync:', err.message);
    } finally {
      setOperationInProgress(null);
    }
  };

  const handleCompletionSync = async () => {
    try {
      setOperationInProgress('completion-sync');
      await cronServerAPIsService.triggerCompletionSync();
      await Promise.all([fetchHealthStatus(), fetchSyncStatus()]);
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to trigger completion sync:', err.message);
    } finally {
      setOperationInProgress(null);
    }
  };

  const handleCleanup = async () => {
    try {
      setOperationInProgress('cleanup');
      await cronServerAPIsService.triggerCleanup();
      await Promise.all([fetchHealthStatus(), fetchSyncStatus()]);
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to trigger cleanup:', err.message);
    } finally {
      setOperationInProgress(null);
    }
  };

  const handleComplianceSync = async () => {
    try {
      setOperationInProgress('compliance-sync');
      await cronServerAPIsService.triggerComplianceSync(complianceDates.from, complianceDates.to, complianceDates.dryRun);
      await Promise.all([fetchHealthStatus(), fetchSyncStatus()]);
    } catch (err: any) {
      // Handle error silently - don't change the view
      console.warn('Failed to trigger compliance sync:', err.message);
    } finally {
      setOperationInProgress(null);
    }
  };

  const handleForceReleaseLocks = async () => {
    try {
      setOperationInProgress('force-release-locks');
      await cronServerAPIsService.forceReleaseLocks();
      await Promise.all([fetchHealthStatus(), fetchSyncStatus()]);
    } finally {
      setOperationInProgress(null);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        // Set initial cron status to Offline, will be updated by fetchHealthStatus
        setCronStatus('Offline');
        await Promise.all([fetchHealthStatus(), fetchSyncStatus(), fetchLogs(latestLogsFirst), fetchLastSyncDate()]);
      } catch (err: any) {
        // Handle error silently - don't change the view
        console.warn('Failed to initialize data:', err.message);
        setCronStatus('Offline');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Auto-refresh logs every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Only fetch logs if they have been successfully fetched before
      if (logsFetched) {
        fetchLogs(latestLogsFirst); // Fetch logs with latest flag based on checkbox state
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [latestLogsFirst, logsFetched]);

  // Auto-refresh all data every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchHealthStatus();
      fetchSyncStatus();
      // Only fetch logs if they have been successfully fetched before
      if (logsFetched) {
        fetchLogs(latestLogsFirst);
      }
      fetchLastSyncDate();
      
    }, 15000); // 15 seconds (15 * 1000 ms)

    return () => clearInterval(interval);
  }, [latestLogsFirst, logsFetched]);

  if (loading) {
    return (
      <div className="loading-container">
        <AppLoading />
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'green';
      case 'degraded': return 'orange';
      case 'has_errors': return 'red';
      default: return 'gray';
    }
  };

  const getOperationStatus = (operation: string | null) => {
    if (!operation) return 'idle';
    if (operation.includes('scheduled-')) return 'scheduled-running';
    return 'manual-running';
  };

  return (
    <div className="cron-server-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Cron Server Management</h2>
          <p>Monitor and manage cron server operations and sync processes</p>
        </div>
        {/* Commented out direct navigation buttons as requested */}
        {/* 
        <div className="navigation-buttons">
          <button 
            onClick={() => window.location.href = '/'}
            className="nav-btn nav-btn-primary"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Go to Dashboards
          </button>
          <button 
            onClick={() => window.location.href = '/stats'}
            className="nav-btn nav-btn-secondary"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
            Go to Cache Statistics
          </button>
        </div>
        */}
      </div>

      <div className="auto-refresh-status">
        <span className="status-indicator">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="status-icon">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Page Auto-refresh: Every 15 seconds
        </span>
        {healthStatus?.timestamp && (
          <div style={{ textAlign: "left", color: '#4a5568', fontWeight: '500', marginTop: 8, fontSize: '0.9rem'}}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '16px', height: '16px', marginBottom: '-2.5px', marginRight: '6px', display: 'inline'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Server Time: {new Date(healthStatus.timestamp).toLocaleString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        )}
        <div style={{ textAlign: "left", color: cronStatus === 'Active' ? '#10b981' : '#ef4444', fontWeight: '600', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px'}}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '20px', height: '20px'}}>
            {cronStatus === 'Active' ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            )}
          </svg>
          Cron Status: {cronStatus}
        </div>
        {lastSyncDate && (
          <div style={{ textAlign: "left", color: '#667eea', fontWeight: '600', marginTop: 10}}>
            Last full sync: {lastSyncDate.toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            })} at {lastSyncDate.toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        )}
        {completionLastSyncDate && (
          <div style={{ textAlign: "left", color: '#667eea', fontWeight: '600', marginTop: 10}}>
            Last completion sync: {completionLastSyncDate.toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            })} at {completionLastSyncDate.toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        )}
      </div>

      <div className="layout-content">

          {/* Health Status Section */}
          {healthStatus && (
            <div className="health-section">
              <div className="section-header">
                <h2>Server Health Status</h2>
                <button
                  onClick={fetchHealthStatus}
                  className="btn btn-secondary"
                  disabled={operationInProgress !== null}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              <div className="health-grid">
                <div className={`health-card ${getStatusColor(healthStatus.status)}`}>
                  <div className="health-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="health-info">
                    <h3>Server Status</h3>
                    <p className="status-value">{healthStatus.status}</p>
                    <p className="status-details">
                      Environment: {healthStatus.environment} | 
                      Uptime: {healthStatus.uptime.days}d {healthStatus.uptime.hours}h {healthStatus.uptime.minutes}m
                    </p>
                  </div>
                </div>

                <div className={`health-card ${getStatusColor(healthStatus.database.status)}`}>
                  <div className="health-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                  </div>
                  <div className="health-info">
                    <h3>Database Status</h3>
                    <p className="status-value">{healthStatus.database.status}</p>
                    {healthStatus.database.pool && (
                      <p className="status-details">
                        Pool: {healthStatus.database.pool.used}/{healthStatus.database.pool.max} used
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Cron Jobs Status */}
              <div className="cron-jobs-section">
                <h3>Cron Jobs Status</h3>
                <div className="cron-jobs-grid">
                  {Object.entries(healthStatus.cronJobs).map(([jobName, job]) => (
                    <div key={jobName} className={`cron-job-card ${getStatusColor(job.status)}`}>
                      <div className="job-header">
                        <h4>{jobName.charAt(0).toUpperCase() + jobName.slice(1)}</h4>
                        <span className={`status-badge ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                      <div className="job-details">
                        <p><strong>Schedule:</strong> {job.schedule}</p>
                        <p><strong>Last Execution:</strong> {job.lastExecution ? new Date(job.lastExecution).toLocaleString() : 'Never'}</p>
                        <p><strong>Next Execution:</strong> {job.nextExecution}</p>
                        <p><strong>Executions:</strong> {job.executionCount} | <strong>Errors:</strong> {job.errorCount}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Sync Operations Section */}
          {syncStatus && (
            <div className="sync-section">
              <div className="section-header">
                <h2>Sync Operations</h2>
                <button
                  onClick={fetchSyncStatus}
                  className="btn btn-secondary"
                  disabled={operationInProgress !== null}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              <div className="sync-status-grid">
                <div className={`sync-status-card ${getOperationStatus(syncStatus.syncStatus.currentSyncOperation)}`}>
                  <div className="status-header">
                    <h3>Current Operation</h3>
                    <span className={`status-badge ${getOperationStatus(syncStatus.syncStatus.currentSyncOperation)}`}>
                      {syncStatus.syncStatus.currentSyncOperation || 'Idle'}
                    </span>
                  </div>
                  <div className="status-details">
                    <p><strong>Start Time:</strong> {syncStatus.syncStatus.syncStartTime ? new Date(syncStatus.syncStatus.syncStartTime).toLocaleString() : 'N/A'}</p>
                    <p><strong>Duration:</strong> {syncStatus.syncStatus.syncDuration || 'N/A'}</p>
                    <p><strong>Can Start New Sync:</strong> {syncStatus.syncStatus.canStartNewSync ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                <div className="sync-operations-grid">
                  <button
                    onClick={handleFullSync}
                    disabled={operationInProgress !== null || !syncStatus.syncStatus.canStartNewSync}
                    className="btn btn-primary"
                  >
                    {operationInProgress === 'full-sync' ? (
                      <>
                        <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Running...
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Full Sync
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleCompletionSync}
                    disabled={operationInProgress !== null || !syncStatus.syncStatus.canStartNewSync}
                    className="btn btn-primary"
                  >
                    {operationInProgress === 'completion-sync' ? (
                      <>
                        <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Running...
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Completion Sync
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleCleanup}
                    disabled={operationInProgress !== null || !syncStatus.syncStatus.canStartNewSync}
                    className="btn btn-primary"
                  >
                    {operationInProgress === 'cleanup' ? (
                      <>
                        <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Running...
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Cleanup
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleForceReleaseLocks}
                    disabled={operationInProgress !== null}
                    className="btn btn-danger"
                  >
                    {operationInProgress === 'force-release-locks' ? (
                      <>
                        <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Running...
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Force Release Locks
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Logs Section */}
          <div className="logs-section" ref={logsSectionRef}>
            <div className="section-header">
              <h2>Server Logs</h2>
              <div className="logs-controls">
                <button
                  onClick={() => fetchLogs(latestLogsFirst)}
                  className="btn btn-secondary"
                  disabled={operationInProgress !== null || logsLoading}
                >
                  {logsLoading ? (
                    <>
                      <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </>
                  )}
                </button>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={latestLogsFirst}
                    onChange={(e) => {
                      // Update state immediately for instant UI feedback
                      setLatestLogsFirst(e.target.checked);
                      // Fetch logs in background without blocking UI
                      fetchLogs(e.target.checked);
                    }}
                    className="form-checkbox"
                  />
                  Latest logs first {logsLoading && <span style={{color: '#667eea', fontWeight: '600'}}>...</span>}
                </label>
              </div>
            </div>
            <div className="logs-container">
              <pre className="logs-content">{logs || 'No logs available'}</pre>
            </div>
          </div>
          {/* Compliance Sync Section */}
          <div className="compliance-section">
            <div className="section-header">
              <h2>User Compliance History Sync</h2>
            </div>
            <div className="compliance-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="from-date">From Date <span className="required">*</span></label>
                  <input
                    type="date"
                    id="from-date"
                    value={complianceDates.from}
                    onChange={(e) => {
                      const fromDate = e.target.value;
                      setComplianceDates(prev => ({ 
                        ...prev, 
                        from: fromDate,
                        // If to date is before the new from date, clear it
                        to: prev.to && fromDate && prev.to < fromDate ? '' : prev.to
                      }));
                    }}
                    onClick={(e) => {
                      // Ensure the calendar opens when clicking anywhere on the input
                      e.currentTarget.showPicker?.();
                    }}
                    max={complianceDates.to || new Date().toISOString().split('T')[0]}
                    className="form-input"
                    required
                    autoComplete="off"
                    placeholder="Select start date"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="to-date">To Date <span className="required">*</span></label>
                  <input
                    type="date"
                    id="to-date"
                    value={complianceDates.to}
                    onChange={(e) => setComplianceDates(prev => ({ ...prev, to: e.target.value }))}
                    onClick={(e) => {
                      // Ensure the calendar opens when clicking anywhere on the input
                      e.currentTarget.showPicker?.();
                    }}
                    min={complianceDates.from || undefined}
                    max={new Date().toISOString().split('T')[0]}
                    className="form-input"
                    required
                    autoComplete="off"
                    placeholder="Select end date"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={complianceDates.dryRun}
                      onChange={(e) => setComplianceDates(prev => ({ ...prev, dryRun: e.target.checked }))}
                      className="form-checkbox"
                    />
                    Dry Run
                  </label>
                </div>
              </div>
              {complianceDates.from && complianceDates.to && complianceDates.from > complianceDates.to && (
                <div className="form-error">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '16px', height: '16px', marginRight: '8px'}}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  To Date must be after From Date
                </div>
              )}
              <button
                onClick={handleComplianceSync}
                disabled={
                  operationInProgress !== null || 
                  !complianceDates.from || 
                  !complianceDates.to ||
                  complianceDates.from > complianceDates.to
                }
                className="btn btn-primary"
              >
                {operationInProgress === 'compliance-sync' ? (
                  <>
                    <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Running...
                  </>
                ) : (
                  <>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Sync Compliance Records
                  </>
                )}
              </button>
            </div>
          </div>

      </div>
    </div>
  );
};

export default CronServerPage;